// v2.15.0 — Page /mon-compte : refactor pour le nouveau format adresses v3.
// v2.15.2 — Refonte UX : Domicile via GPS (1-click), Autres via paste, boutons cards enrichis.
// v2.19.22f-2 — Wrap dans un shell 5 tabs (Adresses / Conversations / Docs / Livraisons / Favoris).

import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
// [H_UIPOLISH_V1_L3 sous-palier 2.B] Polish-only : framer-motion pour tab underline animé.
import { m } from "framer-motion";
import useUserId from "../hooks/useUserId";
import FiscalDocsTab from "../components/FiscalDocsTab";
// [H_UIPOLISH_V1_L4] Polish-only : visual avatar illustré + pattern wax.
import AyaAsset from "@/components/ui/AyaAsset";
import AvatarUploader from "@/components/AvatarUploader";
import DomicileGeolocModal from "../components/DomicileGeolocModal";
import AyaPriveAuthorizations from "../components/AyaPriveAuthorizations";
import { setSpace as setProSpace, getProProfile } from "@/pro/proSpace";
import OtherAddressPasteModal from "../components/OtherAddressPasteModal";
import AddressRequestShareModal from "../components/AddressRequestShareModal";
import TransportEstimateModal from "../components/TransportEstimateModal";
import AuthModal from "@/components/AuthModal";
import FavoritesTab from "@/components/FavoritesTab";
import OwnerInviteBlock from "@/components/OwnerInviteBlock";
import OwnerShopsStatus from "@/components/OwnerShopsStatus";
import CourierAccessBlock from "@/components/CourierAccessBlock";
import SettingsTab from "@/components/SettingsTab";
import MyNamedAddressesSection from "@/components/MyNamedAddressesSection";
import WalletQuickLink from "@/components/WalletQuickLink";

import { useAuth } from "@/hooks/useAuth";
import MonCompteHero from "@/components/MonCompteHero";
import "@/MonComptePage.css";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const MC_TABS = [
  { id: "addresses", emoji: "🗺", label: "Adresses CIV" },
  { id: "billets", emoji: "🎫", label: "Mes billets", route: "/mes-billets" },
  { id: "conversations", emoji: "💬", label: "Conversations" },
  { id: "uploads", emoji: "📎", label: "Documents" },
  { id: "recus", emoji: "🧾", label: "Reçus fiscaux" },
  { id: "deliveries", emoji: "🚚", label: "Livraisons" },
  { id: "favorites", emoji: "⭐", label: "Favoris" },
  { id: "aya_prive", emoji: "🤖", label: "AYA Privé" },
  { id: "settings", emoji: "⚙️", label: "Paramètres" },
];

function StubTab({ icon, title, sub }) {
  return (
    <div className="mc-empty">
      <div className="mc-empty-icon">{icon}</div>
      <div className="mc-empty-title">{title}</div>
      <div className="mc-empty-sub">{sub}</div>
    </div>
  );
}

// v3.0.0-rc.8h — AdminTokenReveal : copy-paste rapide du x-admin-token pour
// les dashboards protégés (Console Partenaires, Gouvernance). Token affiché
// en clair sur demande, préremplit `localStorage.civ_ops_token` en 1 clic.
function AdminTokenReveal() {
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  const [token, setToken] = useState(() => {
    try { return localStorage.getItem("civ_ops_token") || "civ-ai-admin-2026"; }
    catch (_) { return "civ-ai-admin-2026"; }
  });
  const doCopy = async () => {
    try {
      await navigator.clipboard.writeText(token);
      localStorage.setItem("civ_ops_token", token);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (_) { /* noop */ }
  };
  return (
    <div
      data-testid="admin-token-reveal"
      style={{
        marginTop: 14, padding: 12, borderRadius: 12,
        background: "rgba(0,0,0,0.25)",
        border: "1px dashed rgba(251,191,36,0.4)",
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, marginBottom: 8, flexWrap: "wrap" }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: "#FCD34D", letterSpacing: 0.4, textTransform: "uppercase" }}>
          🔑 Token admin (x-admin-token)
        </div>
        <button
          data-testid="admin-token-toggle"
          onClick={() => setRevealed((v) => !v)}
          style={{ padding: "4px 10px", borderRadius: 6, fontSize: 11, background: "transparent", color: "#FCD34D", border: "1px solid rgba(251,191,36,0.4)", cursor: "pointer", fontWeight: 700 }}
        >{revealed ? "👁 Masquer" : "👁 Révéler"}</button>
      </div>
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
        <input
          type={revealed ? "text" : "password"}
          value={token}
          onChange={(e) => setToken(e.target.value)}
          data-testid="admin-token-input"
          style={{ flex: 1, minWidth: 200, padding: "8px 10px", borderRadius: 8, background: "rgba(0,0,0,0.3)", border: "1px solid rgba(251,191,36,0.25)", color: "#FCD34D", fontFamily: "ui-monospace, Menlo, monospace", fontSize: 12 }}
        />
        <button
          data-testid="admin-token-copy"
          onClick={doCopy}
          style={{ padding: "8px 14px", borderRadius: 8, background: copied ? "#10B981" : "linear-gradient(135deg, #FCD34D, #F59E0B)", color: copied ? "#fff" : "#3A2E10", border: "none", cursor: "pointer", fontWeight: 800, fontSize: 12 }}
        >{copied ? "✓ Copié" : "📋 Copier + activer"}</button>
      </div>
      <p style={{ margin: "8px 2px 0", fontSize: 11, color: "rgba(255,255,255,0.55)", lineHeight: 1.55 }}>
        Ce token débloque les dashboards protégés (Console Partenaires, Gouvernance).
        Le clic <strong>&laquo;&nbsp;Copier + activer&nbsp;&raquo;</strong> le charge automatiquement dans le navigateur
        (<code>localStorage.civ_ops_token</code>), les pages ops le retrouvent sans nouvelle saisie.
      </p>
    </div>
  );
}

// v3.17.1 — DemoResetBlock : purge les données de test (commandes, leads,
// notifications, paniers famille) pour repartir propre avant un pitch
// investisseur. Comptes, wallets, adresses et boutiques sont conservés.
function DemoResetBlock() {
  const [state, setState] = useState("idle"); // idle | loading | done | error
  const [summary, setSummary] = useState(null);
  const run = async () => {
    if (!window.confirm("Reset Démo : effacer toutes les commandes, notifications, leads et paniers famille de test ?\n(Comptes, wallets, adresses et boutiques sont conservés.)")) return;
    setState("loading");
    setSummary(null);
    try {
      const API = process.env.REACT_APP_BACKEND_URL || "";
      const tok = localStorage.getItem("civ_ops_token") || "civ-ai-admin-2026";
      const r = await fetch(`${API}/api/v3/admin/demo-reset`, { method: "POST", headers: { "x-admin-token": tok } });
      const d = await r.json();
      if (!r.ok || !d.ok) throw new Error(d.error || "reset impossible");
      const nonZero = Object.entries(d.cleared || {}).filter(([, v]) => Number(v) > 0);
      const total = nonZero.reduce((s, [, v]) => s + Number(v), 0);
      setSummary(total > 0
        ? `${total} documents effacés — ${nonZero.map(([k, v]) => `${k}: ${v}`).join(", ")}`
        : "Rien à nettoyer, la démo est déjà propre.");
      setState("done");
    } catch (e) {
      setSummary(e?.message || "erreur");
      setState("error");
    }
  };
  return (
    <div
      data-testid="demo-reset-block"
      style={{
        marginTop: 14, padding: 12, borderRadius: 12,
        background: "rgba(0,0,0,0.25)",
        border: "1px dashed rgba(239,68,68,0.45)",
      }}
    >
      <div style={{ fontSize: 12, fontWeight: 700, color: "#FCA5A5", letterSpacing: 0.4, textTransform: "uppercase", marginBottom: 8 }}>
        🔄 Reset Démo
      </div>
      <p style={{ margin: "0 0 10px", fontSize: 11, color: "rgba(255,255,255,0.55)", lineHeight: 1.55 }}>
        Efface les <strong>commandes, notifications, leads et paniers famille</strong> de test pour repartir
        sur une app propre avant un pitch. Comptes, wallets, adresses, boutiques et inscriptions pro sont <strong>conservés</strong>.
      </p>
      <button
        data-testid="demo-reset-btn"
        onClick={run}
        disabled={state === "loading"}
        style={{
          padding: "9px 16px", borderRadius: 8,
          background: state === "loading" ? "rgba(239,68,68,0.4)" : "linear-gradient(135deg, #EF4444, #B91C1C)",
          color: "#fff", border: "none", cursor: "pointer", fontWeight: 800, fontSize: 12,
        }}
      >{state === "loading" ? "⏳ Nettoyage…" : "🔄 Lancer le Reset Démo"}</button>
      {summary && (
        <div data-testid="demo-reset-summary" style={{ marginTop: 8, fontSize: 11.5, color: state === "error" ? "#FCA5A5" : "#6EE7B7" }}>
          {state === "error" ? "❌ " : "✅ "}{summary}
        </div>
      )}
    </div>
  );
}

export default function MonComptePage() {
  const userId = useUserId();
  const { user, isAuthenticated, logout } = useAuth();
  const [activeTab, setActiveTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      return t && MC_TABS.some((x) => x.id === t) ? t : "addresses";
    } catch (_) { return "addresses"; }
  });
  const [authOpen, setAuthOpen] = useState(false);
  // AYA PRO Phase 1 — l'espace pro a déménagé vers /pro (page Mon Compte = perso uniquement).
  const topMode = "perso";
  const [home, setHome] = useState(null);
  const [others, setOthers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [domicileOpen, setDomicileOpen] = useState(false);
  const [otherOpen, setOtherOpen] = useState(false);
  const [requestShareOpen, setRequestShareOpen] = useState(false);
  const [transportFor, setTransportFor] = useState(null);

  const load = useCallback(async () => {
    if (!userId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/v3/addresses`, { params: { user_id: userId } });
      setHome(r.data.home || null);
      setOthers(r.data.others || []);
    } catch (_) {
      toast.error("Impossible de charger les adresses");
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  // v2.15.5 — Polling 30s sur les address-requests fulfilled.
  useEffect(() => {
    if (!userId) return;
    let lastCheck = new Date().toISOString();
    const interval = setInterval(async () => {
      try {
        const r = await axios.get(`${API}/v3/address-requests/poll`, { params: { user_id: userId, since: lastCheck } });
        const newOnes = r.data?.new_fulfilled || [];
        if (newOnes.length > 0) {
          for (const req of newOnes) {
            toast.success(`✅ ${req.returned_contact_name || "Ton contact"} a partagé son adresse !`);
          }
          load();
        }
        lastCheck = new Date().toISOString();
      } catch (_e) { /* silencieux */ }
    }, 30000);
    return () => clearInterval(interval);
  }, [userId, load]);

  const openAddHome = () => setDomicileOpen(true);
  const openAddOther = () => setOtherOpen(true);

  const onDelete = async (a) => {
    if (a.is_home) { toast.error("Le Domicile principal n'est pas supprimable directement"); return; }
    if (!window.confirm(`Supprimer l'adresse "${a.name}" ?`)) return;
    try {
      const r = await axios.delete(`${API}/v3/addresses/${a.id}`);
      if (!r.data.ok) throw new Error(r.data.reason || "delete_failed");
      toast.success("Adresse supprimée");
      load();
    } catch (e) {
      toast.error(e.response?.data?.reason || e.message);
    }
  };

  const onSetHome = async (a) => {
    try {
      const r = await axios.patch(`${API}/v3/addresses/${a.id}`, { is_home: true });
      if (!r.data.ok) throw new Error(r.data.reason || "patch_failed");
      toast.success("📍 Domicile principal mis à jour");
      load();
    } catch (e) {
      toast.error(e.response?.data?.reason || e.message);
    }
  };

  const yangoDeepLink = (a) => {
    // Tentative deeplink natif Yango, fallback web après 1.5s.
    const labelEnc = encodeURIComponent(a.description || a.name);
    const nativeUrl = `yango://route?dest_lat=${a.lat}&dest_lng=${a.lng}&dest_label=${labelEnc}`;
    const webUrl = `https://yango.com/?utm_source=civ-ai&dest_lat=${a.lat}&dest_lng=${a.lng}`;
    const t0 = Date.now();
    window.location.href = nativeUrl;
    setTimeout(() => {
      if (Date.now() - t0 < 1800 && document.visibilityState === "visible") {
        toast.message("Yango app non détectée — ouverture de yango.com");
        window.open(webUrl, "_blank");
      }
    }, 1500);
  };

  const carGps = (a) => {
    const labelEnc = encodeURIComponent(a.description || a.name);
    const codeEnc = encodeURIComponent(a.address_civ || "");
    window.location.href = `/navigate?dest=${a.lat},${a.lng}&code=${codeEnc}&label=${labelEnc}`;
  };

  return (
    <div data-testid="mon-compte-page" className="mcx-page">
      <div style={{ maxWidth: 720, margin: "0 auto" }}>
        {/* ── ENTÊTE AYA officielle (masque + logo + tagline) ── */}
        <header data-testid="mc-aya-header" style={{ textAlign: "center", padding: "0 0 12px", position: "relative" }}>
          <img src="/aya/splash.png" alt="Masque AYA" style={{ width: 56, height: 56, objectFit: "contain", display: "block", margin: "0 auto", filter: "drop-shadow(0 0 10px rgba(212,175,55,0.35))" }} />
          <div style={{ fontSize: 21, fontWeight: 900, letterSpacing: 3, color: "#0E3B2A", marginTop: -7, lineHeight: 1 }}>AYA</div>
          <div style={{ fontSize: 7.5, letterSpacing: 3, color: "#6B7280", fontWeight: 700, marginTop: 3 }}>L'AFRIQUE AU QUOTIDIEN</div>
        </header>
        {/* 2026-06 — Refonte profil selon maquette validée (hero dark + sections claires). */}
        <MonCompteHero
          user={user}
          isAuthenticated={isAuthenticated}
          home={home}
          others={others}
          navigate={(to) => (window.location.href = to)}
          onOpenTab={setActiveTab}
          onEditHome={() => setDomicileOpen(true)}
          onLogin={() => setAuthOpen(true)}
          onLogout={async () => {
            if (window.confirm("Te déconnecter de AYA ?")) {
              await logout();
              window.location.href = "/";
            }
          }}
        />
        {(!isAuthenticated || localStorage.getItem("civ_is_guest") === "1" || String(user?.phone || "").startsWith("guest")) && (
          <div className="mc-auth-cta" style={{ margin: "0 0 12px", textAlign: "center" }}>
            <button className="mc-link-btn" onClick={() => setAuthOpen(true)} data-testid="mc-login-btn">
              Se connecter pour sauvegarder
            </button>
          </div>
        )}
        {user?.role !== "commerce" && user?.role !== "owner" && (
          <div style={{ textAlign: "center", margin: "0 0 12px" }}>
            <a
              href="/inscription-pro"
              data-testid="mc-become-pro-link"
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 8,
                padding: "8px 14px",
                borderRadius: 10,
                background: "linear-gradient(135deg, rgba(16,185,129,0.16), rgba(16,185,129,0.08))",
                border: "1px solid rgba(16,185,129,0.4)",
                color: "#10B981",
                fontSize: 13,
                fontWeight: 700,
                textDecoration: "none",
              }}
            >
              💼 Je suis un pro — m'inscrire sur AYA
            </a>
          </div>
        )}

        {/* AYA PRO Phase 1 — le toggle Perso/Pro et MonComptePro ont déménagé vers /pro/compte. */}

        <div className="mcx-panel">

        {/* Vue Perso : tout le contenu existant (inchangé). */}
        {topMode === "perso" && (<>

        {/* v2.19.28 — Section Dashboards B2B (visible uniquement role=owner). */}
        {user?.role === "owner" && (
          <section data-testid="moncompte-owner-b2b" style={{
            margin: "16px 0 24px",
            padding: "16px 18px",
            background: "linear-gradient(135deg, rgba(251,191,36,0.14), rgba(245,158,11,0.06))",
            border: "1px solid rgba(251,191,36,0.4)",
            borderRadius: 14,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
              <span style={{ fontSize: 20 }}>🛠️</span>
              <h2 style={{ margin: 0, fontSize: 17, color: "#D4AF37", fontWeight: 700 }}>Dashboards B2B</h2>
              <span style={{
                fontSize: 10, padding: "2px 8px", borderRadius: 999,
                background: "rgba(251,191,36,0.22)", color: "#FCD34D",
                fontWeight: 700, letterSpacing: 0.5, textTransform: "uppercase",
              }}>owner</span>
            </div>
            <p style={{ margin: "0 0 12px", fontSize: 12, color: "rgba(255,255,255,0.7)" }}>
              Accès complet aux outils internes (CRUD répertoires, carte de couverture, ops).
            </p>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))", gap: 8 }}>
              <a href="/internal" data-testid="owner-b2b-internal" className="mc-owner-card" style={{ ...ownerLinkStyle, gridColumn: "1 / -1", border: "1px solid rgba(46,226,123,0.55)", color: "#2EE27B", background: "rgba(46,226,123,0.08)", fontWeight: 800 }}>⌘ AYA Internal · Command Center</a>
              <a href="/ops" data-testid="owner-b2b-ops" className="mc-owner-card" style={ownerLinkStyle}>📊 OPS principal</a>
              <a href="/ops/live" data-testid="owner-b2b-live-ops" className="mc-owner-card" style={{ ...ownerLinkStyle, border: "1px solid rgba(0,255,136,0.45)", color: "#00FF88" }}>🛰 Salle Ops Live</a>
              <a href="/ops-b2b" data-testid="owner-b2b-ops-b2b" className="mc-owner-card" style={ownerLinkStyle}>🗂 OPS B2B (CRUD)</a>
              <a href="/carte-publique" data-testid="owner-b2b-coverage" className="mc-owner-card" style={ownerLinkStyle}>🗺 Carte de Couverture</a>
              <a href="/internal/operations/pro-validation" data-testid="owner-b2b-inscriptions" className="mc-owner-card" style={ownerLinkStyle}>📥 Inscriptions Pro</a>
              <a href="/ops/impressions" data-testid="owner-b2b-impressions" className="mc-owner-card" style={ownerLinkStyle}>📊 Top fiches LLM</a>
              <a href="/ops/digests-mensuels" data-testid="owner-b2b-digests" className="mc-owner-card" style={ownerLinkStyle}>📨 Digests Mensuels</a>
              {/* v3.0.0-rc.8h — Nouveaux dashboards ajoutés au raccourci owner. */}
              <a href="/ops-partners" data-testid="owner-b2b-partners" className="mc-owner-card" style={ownerLinkStyle}>📦 Console Partenaires</a>
              <a href="/governance" data-testid="owner-b2b-gouvernance" className="mc-owner-card" style={ownerLinkStyle}>⚖️ Dashboard Gouvernance</a>
              {/* v3.0.0-rc.8t — AYA Live : commerce en direct via QR overlay. */}
              <a href="/pro/live" data-testid="owner-b2b-live" className="mc-owner-card" style={{ ...ownerLinkStyle, background: "linear-gradient(135deg, rgba(239,68,68,0.14), rgba(255,126,27,0.14))", borderColor: "rgba(239,68,68,0.4)" }}>🎥 AYA Live</a>
              {/* 2026-06 — Dashboards Transport côté professionnel : hub 4 onglets (taxi, car, avion, VTC). */}
              <a href="/transport-pro" data-testid="owner-b2b-transport-pro" className="mc-owner-card" style={{ ...ownerLinkStyle, border: "1px solid rgba(245,196,69,0.5)", color: "#F5C445", background: "rgba(245,196,69,0.08)" }}>🚖 Transport Pro · Taxi / Car / Avion / VTC</a>
            </div>
            {/* v3.0.0-rc.8h — Token admin à copier-coller (pour les dashboards protégés x-admin-token). */}
            <AdminTokenReveal />
            {/* v3.17.1 — Reset Démo (owner) : app propre entre deux pitchs. */}
            <DemoResetBlock />
            {/* v2.19.29 — Bloc invitations partenaires (owner-only) */}
            <OwnerInviteBlock />
            {/* 2026-07-08 — Qui est en ligne (mini-sites) — visibilité owner. */}
            <OwnerShopsStatus />
            {/* v2.19.30 — Module Livreur owner-only */}
            <CourierAccessBlock />
          </section>
        )}

        {/* v3.18.0 — Raccourci Espace Diaspora (comptes user_type=diaspora). */}
        {user?.user_type === "diaspora" && (
          <section
            data-testid="mc-diaspora-shortcut"
            style={{
              margin: "0 0 16px", padding: "14px 16px", borderRadius: 14,
              background: "linear-gradient(135deg, rgba(239,199,94,0.12), rgba(16,185,129,0.06))",
              border: "1px solid rgba(239,199,94,0.4)",
              display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap",
            }}
          >
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 1.6, color: "#EFC75E" }}>🌍 ESPACE DIASPORA</div>
              <div style={{ fontSize: 12, opacity: 0.65, marginTop: 3 }}>Famille, business, installation, solidarité — tout ce qui compte pour toi, en une page.</div>
            </div>
            <a
              href="/diaspora"
              data-testid="mc-diaspora-open-btn"
              style={{
                padding: "10px 18px", borderRadius: 999, textDecoration: "none",
                background: "linear-gradient(135deg,#EFC75E,#D9A83C)", color: "#1A1206",
                fontWeight: 800, fontSize: 12.5, whiteSpace: "nowrap",
              }}
            >Ouvrir mon tableau de bord</a>
          </section>
        )}

        {/* Avatar : intégré au hero maquette (MonCompteHero). */}


        {/* v2.19.22f-2 — Tabs nav */}
        <nav className="mc-tabs" data-testid="mc-tabs">
          {MC_TABS.map((t) => (
            <button
              key={t.id}
              onClick={() => (t.route ? (window.location.href = t.route) : setActiveTab(t.id))}
              className={`mc-tab ${activeTab === t.id ? "mc-tab-active" : ""}`}
              data-testid={`mc-tab-${t.id}`}
            >
              <span className="mc-tab-emoji">{t.emoji}</span>
              <span className="mc-tab-label">{t.label}</span>
              {/* [H_UIPOLISH_V1_L3 sous-palier 2.B] Underline glissant via layoutId */}
              {activeTab === t.id && (
                <m.span
                  className="mc-tab-underline"
                  layoutId="moncompte-tab-underline"
                  data-testid="mc-tab-underline"
                  transition={{ type: "spring", stiffness: 380, damping: 30 }}
                  aria-hidden="true"
                />
              )}
            </button>
          ))}
        </nav>

        {/* 💼 Mon Wallet — badge déplacé depuis le Chat (demande user, 2026-07-02). */}
        <section data-testid="moncompte-wallet-section" style={{ marginBottom: 24 }}>
          <h2 style={{ margin: "0 0 12px", fontSize: 18 }}>💼 Mon Wallet</h2>
          <WalletQuickLink inline />
        </section>

        {activeTab === "addresses" && (<>
        {/* ─────────────── SECTION 1 — 📍 Mon Adresse (Domicile) ─────────────── */}
        <section data-testid="moncompte-home-section" style={{ marginBottom: 28 }}>
          <h2 style={{ margin: "0 0 14px", fontSize: 18 }}>📍 Mon Adresse</h2>

          {loading && <div style={{ color: "#94a3b8", fontSize: 13 }}>Chargement…</div>}

          {!loading && !home && (
            <div data-testid="moncompte-home-empty" style={{
              background: "rgba(34,197,94,0.06)", border: "1px dashed rgba(34,197,94,0.3)",
              borderRadius: 12, padding: 24, textAlign: "center",
            }}>
              <div style={{ color: "#86EFAC", fontSize: 13, marginBottom: 12 }}>
                Tu n’as pas encore défini ton <strong>Domicile</strong>.
              </div>
              <button
                onClick={openAddHome}
                data-testid="activate-gps-btn"
                style={{
                  background: "linear-gradient(135deg, #F5E9C6 0%, #E4C87F 55%, #CDA452 100%)",
                  color: "#3A2E10",
                  border: "none",
                  padding: "12px 22px",
                  borderRadius: 10,
                  cursor: "pointer",
                  fontWeight: 800,
                  fontSize: 14,
                  boxShadow: "0 4px 14px rgba(205,164,82,0.4)",
                }}
              >📍 Activer ma position GPS pour créer mon Domicile</button>
            </div>
          )}

          {!loading && home && (
            <AddressCardV3
              addr={home}
              onDelete={() => onDelete(home)}
              onYango={() => yangoDeepLink(home)}
              onCarGps={() => carGps(home)}
              onTransport={() => setTransportFor(home)}
              onSetHome={null}
            />
          )}
        </section>

        {/* ─────────────── SECTION 2 — 📌 Mes autres adresses ─────────────── */}
        <section data-testid="moncompte-others-section" style={{ marginBottom: 24 }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, gap: 8, flexWrap: "wrap" }}>
            <h2 style={{ margin: 0, fontSize: 18 }}>📌 Mes autres adresses</h2>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button onClick={() => setRequestShareOpen(true)} data-testid="request-address-btn" style={{
                background: "rgba(232,213,163,0.12)", color: "#EBD9A8", border: "1px solid rgba(232,213,163,0.45)",
                padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontWeight: 600, fontSize: 13,
              }}>💬 Demander une adresse</button>
              <button onClick={openAddOther} data-testid="moncompte-add-other-btn" style={{
                background: "linear-gradient(135deg, #F5E9C6, #CDA452)", color: "#3A2E10", border: "none", padding: "8px 14px",
                borderRadius: 8, cursor: "pointer", fontWeight: 800, fontSize: 13,
              }}>➕ Ajouter</button>
            </div>
          </div>

          {!loading && others.length === 0 && (
            <div data-testid="moncompte-others-empty" style={{
              background: "rgba(255,255,255,0.04)", border: "1px dashed rgba(255,255,255,0.15)",
              borderRadius: 12, padding: 18, textAlign: "center", color: "#94a3b8", fontSize: 13,
            }}>
              Aucune autre adresse. Ajoute Bureau, École, etc.
            </div>
          )}

          {!loading && others.length > 0 && (
            <div data-testid="moncompte-others-list" style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              {others.map((a) => (
                <AddressCardV3
                  key={a.id}
                  addr={a}
                  onDelete={() => onDelete(a)}
                  onYango={() => yangoDeepLink(a)}
                  onCarGps={() => carGps(a)}
                  onTransport={() => setTransportFor(a)}
                  onSetHome={() => onSetHome(a)}
                />
              ))}
            </div>
          )}
        </section>
        {/* v2.19.34 — SECTION 3 : Maisons Nommées (claims "Maison Koffi") */}
        <MyNamedAddressesSection isAuthenticated={isAuthenticated} />
        </>)}

        {activeTab === "conversations" && (
          <StubTab
            icon="💬"
            title="Conversations sauvegardées"
            sub={isAuthenticated
              ? "Bientôt — sauvegarde automatique de tes conversations chat avec l'agent."
              : "Connecte-toi pour activer la sauvegarde de tes conversations."}
          />
        )}

        {activeTab === "uploads" && (
          <StubTab
            icon="📎"
            title="Documents uploadés"
            sub="Les documents que tu envoies dans le chat (PDF, images, textes) sont conservés 15 minutes en mémoire. Une persistance long-terme arrive bientôt."
          />
        )}

        {activeTab === "recus" && (
          <FiscalDocsTab phone={user?.phone} isAuthenticated={isAuthenticated} />
        )}

        {activeTab === "aya_prive" && (
          <AyaPriveAuthorizations userId={userId} />
        )}

        {activeTab === "deliveries" && (
          <StubTab
            icon="🚚"
            title="Aucune livraison en cours"
            sub="Crée-en une depuis le mode Shopping ou Commerces dans le chat."
          />
        )}

        {activeTab === "favorites" && (
          <FavoritesTab onLoginRequest={() => setAuthOpen(true)} />
        )}

        {activeTab === "settings" && (
          <>
            {/* 2026-06 — Accès AYA PRO déplacé ici (badge flottant supprimé). Owner only. */}
            {user?.role === "owner" && (
              <button
                type="button"
                data-testid="moncompte-pro-access-btn"
                onClick={() => {
                  setProSpace("pro");
                  window.location.href = getProProfile() ? "/pro" : "/choix";
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 12, width: "100%", marginBottom: 14,
                  padding: "14px 16px", borderRadius: 16, cursor: "pointer", textAlign: "left",
                  border: "1px solid rgba(212,175,55,0.65)",
                  background: "linear-gradient(135deg, rgba(42,33,16,0.95), rgba(23,18,10,0.95))",
                  color: "#EAD9A6", fontFamily: "inherit",
                }}
              >
                <span style={{ fontSize: 22 }}>💼</span>
                <span style={{ flex: 1 }}>
                  <span style={{ display: "block", fontSize: 14.5, fontWeight: 900 }}>Espace AYA PRO</span>
                  <span style={{ display: "block", fontSize: 11.5, opacity: 0.75, marginTop: 2 }}>Basculer vers mon espace professionnel</span>
                </span>
                <span style={{ fontSize: 16, fontWeight: 900 }}>→</span>
              </button>
            )}
            <SettingsTab />
          </>
        )}

        {/* v2.19.38 — Fin de la branche topMode === "perso" */}
        </>)}
        </div>

        <div className="mcx-userid">
          userId : <code data-testid="moncompte-userid">{userId}</code>
        </div>

      </div>

      <DomicileGeolocModal
        open={domicileOpen}
        userId={userId}
        onClose={() => setDomicileOpen(false)}
        onCreated={() => { setDomicileOpen(false); load(); }}
      />

      <OtherAddressPasteModal
        open={otherOpen}
        userId={userId}
        onClose={() => setOtherOpen(false)}
        onCreated={() => { setOtherOpen(false); load(); }}
      />

      <AddressRequestShareModal
        open={requestShareOpen}
        userId={userId}
        onClose={() => setRequestShareOpen(false)}
      />

      <TransportEstimateModal
        open={!!transportFor}
        destination={transportFor}
        onClose={() => setTransportFor(null)}
      />

      {/* v2.19.22f-2 — Auth modal global */}
      <AuthModal open={authOpen} onClose={() => setAuthOpen(false)} />

      {/* v2.14.4 — Sticky CTA funnel sponsoring (Pro). */}
      {/* 2026-10 — ProStickyCTA retiré de Mon Compte (réservé à l'espace Pro). */}
    </div>
  );
}

function AddressCardV3({ addr, onDelete, onYango, onCarGps, onTransport, onSetHome }) {
  const startGps = () => {
    const params = new URLSearchParams({
      dest: `${addr.lat},${addr.lng}`,
      code: addr.address_civ || "",
      label: addr.name || "",
    });
    window.location.href = `/navigate?${params.toString()}`;
  };

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success(`✅ ${label} copié`);
    } catch {
      toast.error("Impossible de copier (clipboard refusé)");
    }
  };

  const shareWhatsApp = () => {
    const deeplink = `${window.location.origin}/navigate?dest=${addr.lat},${addr.lng}&code=${encodeURIComponent(addr.address_civ)}&label=${encodeURIComponent(addr.name)}`;
    const msg = `📍 ${addr.name}\n🏷️ Code : ${addr.address_civ}\n🗺️ Coords : ${addr.lat}, ${addr.lng}${addr.description ? `\n📝 ${addr.description}` : ""}\n\n🧭 Itinéraire direct : ${deeplink}`;
    window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
  };

  return (
    <div data-testid={`address-card-${addr.id}`} style={{
      background: addr.is_home ? "rgba(34,197,94,0.08)" : "rgba(255,255,255,0.04)",
      border: `1px solid ${addr.is_home ? "rgba(34,197,94,0.3)" : "rgba(255,255,255,0.1)"}`,
      borderRadius: 12, padding: 16, position: "relative",
    }}>
      <div style={{ position: "absolute", top: 12, right: 12, display: "flex", gap: 6 }}>
        {onSetHome && (
          <button onClick={onSetHome} data-testid={`addr-sethome-${addr.id}`} title="Définir comme Domicile" style={iconBtn}>🏠</button>
        )}
        {!addr.is_home && (
          <button onClick={onDelete} data-testid={`addr-delete-${addr.id}`} title="Supprimer" style={iconBtn}>🗑</button>
        )}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>{addr.name}</h3>
        {addr.is_home && (
          <span data-testid="addr-home-badge" style={{
            background: "rgba(34,197,94,0.22)", color: "#86efac", padding: "2px 8px",
            borderRadius: 999, fontSize: 10, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.5,
          }}>🏠 Domicile</span>
        )}
      </div>
      {addr.description && <div style={{ fontSize: 13, color: "#cbd5e1", marginBottom: 8 }}>{addr.description}</div>}

      {/* v2.15.2 — CTA principal: 🚀 Démarrer GPS */}
      <button
        onClick={startGps}
        data-testid={`start-gps-${addr.id}`}
        style={{
          width: "100%",
          padding: "10px 14px",
          background: "linear-gradient(135deg,#C9A227,#D4AF37)",
          color: "#0F172A",
          border: "none",
          borderRadius: 8,
          cursor: "pointer",
          fontSize: 14,
          fontWeight: 700,
          boxShadow: "0 2px 8px rgba(251,191,36,0.4)",
          marginBottom: 10,
        }}
      >🚀 Démarrer GPS</button>

      {/* Zone "Pour le livreur" — code, coords, partage WhatsApp */}
      <div data-testid={`livreur-zone-${addr.id}`} style={{
        background: "rgba(255,255,255,0.04)",
        border: "1px solid rgba(255,255,255,0.08)",
        borderRadius: 8, padding: "8px 10px", marginBottom: 10, fontSize: 11, color: "#94A3B8",
      }}>
        <div style={{ marginBottom: 6, fontWeight: 600, color: "#CBD5E1" }}>Pour le livreur</div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <span>📋 Code : <code data-testid={`addr-civ-${addr.id}`} style={{ color: "#93C5FD", fontWeight: 700 }}>{addr.address_civ}</code></span>
          <button onClick={() => copyToClipboard(addr.address_civ, "Code")} data-testid={`copy-code-${addr.id}`} style={miniBtn}>📋 Copier</button>
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 6 }}>
          <span>📍 Coords : <span style={{ color: "#CBD5E1", fontFamily: "monospace" }}>{addr.lat.toFixed(6)}, {addr.lng.toFixed(6)}</span></span>
          <button onClick={() => copyToClipboard(`${addr.lat.toFixed(6)}, ${addr.lng.toFixed(6)}`, "Coords")} data-testid={`copy-coords-${addr.id}`} style={miniBtn}>📋 Copier</button>
        </div>
        <button onClick={shareWhatsApp} data-testid={`share-whatsapp-${addr.id}`} style={{
          width: "100%", padding: "6px 8px", background: "#25D366", color: "#fff",
          border: "none", borderRadius: 4, cursor: "pointer", fontSize: 11, fontWeight: 600,
        }}>📤 Partager via WhatsApp</button>
      </div>

      {/* Actions navigation secondaires */}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        <button onClick={onYango} data-testid={`addr-yango-${addr.id}`} style={{ ...actionBtn, background: "#FF6B00" }}>
          🚖 Yango
        </button>
        <button onClick={onCarGps} data-testid={`addr-cargps-${addr.id}`} style={{ ...actionBtn, background: "#1E40AF" }}>
          🚗 Voiture
        </button>
        <button onClick={onTransport} data-testid={`addr-transport-${addr.id}`} style={{ ...actionBtn, background: "#16A34A" }}>
          🚐 Transport
        </button>
      </div>
    </div>
  );
}

const miniBtn = {
  padding: "3px 8px", background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)",
  color: "#CBD5E1", borderRadius: 4, cursor: "pointer", fontSize: 10,
};

const iconBtn = {
  background: "rgba(255,255,255,0.06)", border: "1px solid rgba(255,255,255,0.12)",
  color: "#fff", width: 30, height: 30, borderRadius: 6, cursor: "pointer", fontSize: 14,
};
const actionBtn = {
  color: "#fff", border: "none", padding: "8px 14px", borderRadius: 8, cursor: "pointer",
  fontWeight: 600, fontSize: 13,
};

// v2.19.28 — Style des liens owner B2B.
const ownerLinkStyle = {
  display: "block",
  textAlign: "center",
  padding: "10px 12px",
  background: "rgba(15,23,42,0.55)",
  border: "1px solid rgba(251,191,36,0.4)",
  borderRadius: 10,
  textDecoration: "none",
  color: "#D4AF37",
  fontWeight: 700,
  fontSize: 13,
};
